import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function useAiSuggestions() {
  const { authAxios } = useAuth();
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);
  const lastTitleRef = useRef('');

  const fetchSuggestions = useCallback(async (title, content) => {
    if (!title || title.trim().length < 4) {
      setSuggestions(null);
      return;
    }
    if (title.trim() === lastTitleRef.current) return;
    lastTitleRef.current = title.trim();

    setLoading(true);
    setDismissed(false);
    try {
      const api = authAxios();
      const res = await api.post('/ai/suggest-category', { title: title.trim(), content: content || '' });
      const data = res.data;
      if (data.category || data.location || (data.tags && data.tags.length > 0)) {
        setSuggestions(data);
      } else {
        setSuggestions(null);
      }
    } catch {
      setSuggestions(null);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const debouncedFetch = useCallback((title, content) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(title, content), 1500);
  }, [fetchSuggestions]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setSuggestions(null);
  }, []);

  const reset = useCallback(() => {
    setSuggestions(null);
    setLoading(false);
    setDismissed(false);
    lastTitleRef.current = '';
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { suggestions, loading, dismissed, debouncedFetch, dismiss, reset };
}
